import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  Table,
  Container,
  Row,
  Col,
  InputGroup,
  FormControl,
  Pagination,
  Button,
} from "react-bootstrap";
import AdminHeader from "./Admin/AdminHeader";
import "../css/MasterActivity.css";
import { firestore } from "../Common/firebase";
import apiService from "../services/api.service";
import Swal from "sweetalert2";

const MasterActivity = () => {
  const [users, setUsers] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7;

  useEffect(() => {
    fetchUsersWithCourses();
  }, []);

  // Function to send email reminder to admin
  const scheduleEmailReminder = async (userEmail) => {
    try {
      await apiService.dataClearingMail(userEmail);
      console.log(`Email reminder sent to ${userEmail}`);
    } catch (error) {
      console.error(`Failed to send email reminder to ${userEmail}:`, error);
    }
  };

  // Fetch users and their purchased courses
  const fetchUsersWithCourses = async () => {
    try {
      const usersSnapshot = await firestore.collection("users").get();
      const now = new Date();
      const rowsData = [];

      await Promise.all(
        usersSnapshot.docs.map(async (doc) => {
          const userData = doc.data();
          const coursesSnapshot = await firestore
            .collection("users")
            .doc(doc.id)
            .collection("purchasedCourses")
            .get();

          const purchasedCourses = await Promise.all(
            coursesSnapshot.docs.map(async (courseDoc) => {
              const courseData = courseDoc.data();
              const expirationDate = courseData.expirationDate
                ? new Date(courseData.expirationDate.seconds * 1000)
                : null;

              let totalLessons = 0;
              try {
                const response = await apiService.getCourseById(
                  courseData.courseId
                );
                if (response?.data?.course) {
                  totalLessons = response.data.course.lessons.length;
                }
              } catch (error) {
                console.error("Error fetching course details:", error);
              }

              const completedLessons = courseData.completedLesson || 0;

              if (expirationDate && now > expirationDate) {
                return {
                  ...courseData,
                  userId: doc.id,
                  purchasedCourseDocId: courseDoc.id,
                  courseTitle: courseData.courseTitle,
                  totalLessons,
                  completedLessons,
                  status: "Expired",
                };
              }

              return {
                ...courseData,
                userId: doc.id,
                purchasedCourseDocId: courseDoc.id,
                courseTitle: courseData.courseTitle,
                totalLessons,
                completedLessons,
                status: "Active",
              };
            })
          );

          // Send email immediately for testing
          await scheduleEmailReminder(userData.email);

          if (purchasedCourses.length > 0) {
            purchasedCourses.forEach((course) => {
              rowsData.push({
                email: userData.email,
                lastSignInTime: userData.lastSignInTime,
                ...course,
                purchasedCoursesCount: purchasedCourses.length,
              });
            });
          } else {
            rowsData.push({
              email: userData.email,
              lastSignInTime: userData.lastSignInTime,
              courseTitle: "N/A",
              purchasedCoursesCount: 0,
            });
          }
        })
      );

      const sortedRows = rowsData.sort((a, b) => {
        const dateA = userDataToDate(a.lastSignInTime);
        const dateB = userDataToDate(b.lastSignInTime);
        return dateB - dateA;
      });

      setUsers(sortedRows);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // Convert Firestore timestamp to Date
  const userDataToDate = (timestamp) => {
    if (!timestamp) return 0;
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    return new Date(timestamp);
  };

  const handleSearchChange = (e) => setSearchEmail(e.target.value);
  const filteredRows = users.filter((row) =>
    row.email?.toLowerCase().includes(searchEmail.toLowerCase())
  );

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Calculate days left until expiration
  const calculateDaysLeft = (expirationDate) => {
    if (!expirationDate?.seconds) return "";
    const now = new Date();
    const expiration = new Date(expirationDate.seconds * 1000);
    return Math.max(0, Math.ceil((expiration - now) / (1000 * 60 * 60 * 24)));
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = userDataToDate(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "Africa/Lagos", // UTC+1 (WAT)
    });
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (timestamp) => {
    if (!timestamp) return "";
    const date = userDataToDate(timestamp);
    return date.toISOString().split("T")[0];
  };

  // Format time for input (HH:mm:ss)
  const formatTimeForInput = (timestamp) => {
    if (!timestamp) return "23:59:59";
    const date = userDataToDate(timestamp);
    const hours = String(date.getUTCHours() + 1).padStart(2, "0"); // UTC+1
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  // Handle extend expirations
  const handleExtend = (userId, purchasedCourseDocId, currentExpiration) => {
    // Get tomorrow's date for the min attribute
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    Swal.fire({
      title: "Update Course Expiration",
      html:
        '<label for="swal-date">Select new expiration date</label>' +
        '<input id="swal-date" class="swal2-input" type="date" min="' +
        tomorrow.toISOString().split("T")[0] +
        '">' +
        '<label for="swal-time">Select new expiration time (UTC+1)</label>' +
        '<input id="swal-time" class="swal2-input" type="time" step="1">',
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#70ad46",
      cancelButtonColor: "#dc3545",
      customClass: {
        confirmButton: "custom-confirm-button",
      },
      didOpen: () => {
        document.getElementById("swal-date").value =
          formatDateForInput(currentExpiration);
        document.getElementById("swal-time").value =
          formatTimeForInput(currentExpiration);
      },
      inputValidator: (value) => {
        const dateInput = document.getElementById("swal-date").value;
        const timeInput = document.getElementById("swal-time").value;
        if (!dateInput || !timeInput) {
          return "You need to select both a date and time!";
        }
        const newDateTime = new Date(`${dateInput}T${timeInput}+01:00`);
        const now = new Date();
        // Ensure the selected date is strictly after today
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const selectedDate = new Date(
          newDateTime.getFullYear(),
          newDateTime.getMonth(),
          newDateTime.getDate()
        );

        if (isNaN(newDateTime.getTime())) {
          return "Invalid date or time format!";
        }
        if (selectedDate <= today || newDateTime <= now) {
          return "Expiration date and time must be in the future (after today)!";
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const dateInput = document.getElementById("swal-date").value;
          const timeInput = document.getElementById("swal-time").value;
          const newDate = new Date(`${dateInput}T${timeInput}+01:00`);
          await apiService.updateCourseExpiration({
            userId,
            courseId: purchasedCourseDocId,
            newExpirationDate: newDate.toISOString(),
          });
          Swal.fire("Success", "Expiration date and time updated.", "success");
          fetchUsersWithCourses();
        } catch (error) {
          console.error("Error updating expiration:", error);
          Swal.fire("Error", "Failed to update expiration date.", "error");
        }
      }
    });
  };

  // Export to Excel with timestamp in filename
  const exportToExcel = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-"); // Format: YYYY-MM-DDTHH-MM-SS
    const fileName = `user_activity_data_${timestamp}.xlsx`;

    const formattedData = users.map((row) => ({
      Email: row.email,
      "Last Signed In": formatDate(row.lastSignInTime),
      "Course Title": row.courseTitle,
      "Purchase Date": formatDate(row.purchaseDate),
      "Expiration Date": formatDate(row.expirationDate),
      "Days Left": calculateDaysLeft(row.expirationDate),
      Status: row.status || "N/A",
      "Completed Lessons":
        row.totalLessons == null || row.completedLessons == null
          ? "N/A"
          : row.completedLessons === 0
          ? `0 of ${row.totalLessons}`
          : `${row.completedLessons} of ${row.totalLessons}`,
      Score:
        row.purchasedCoursesCount === 0
          ? "N/A"
          : row.score == null
          ? 0
          : row.score,
      "Number of Purchased Courses":
        row.purchasedCoursesCount == null || row.purchasedCoursesCount === 0
          ? "N/A"
          : row.purchasedCoursesCount,
    }));

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <AdminHeader />
      <Container className="mt-4">
        <Row className="mb-3">
          <Col>
            <h3 className="font-weight-bold text-uppercase">User Activity</h3>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col sm={4}>
            <InputGroup>
              <FormControl
                placeholder="Search by email"
                value={searchEmail}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Col>
          <Col xs={12} sm={6} md={4} lg={2}>
            <Button
              variant="success"
              onClick={exportToExcel}
              style={{ height: "63%", padding: "0.375rem 0.75rem" }}
            >
              Export to Excel
            </Button>
          </Col>
        </Row>
        <div className="table-responsive">
          <Table className="custom-table" bordered hover>
            <thead>
              <tr>
                <th>Email</th>
                <th>Last Signed In</th>
                <th>Course Title</th>
                <th>Purchase Date</th>
                <th>Expiration Date</th>
                <th>Status</th>
                <th className="text-center">Days Left</th>
                <th className="text-center">Completed Lessons</th>
                <th>Score</th>
                <th className="text-center">Number of Purchased Courses</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((row, index) => (
                <tr key={index}>
                  <td>{row.email}</td>
                  <td>
                    {row.lastSignInTime ? formatDate(row.lastSignInTime) : ""}
                  </td>
                  <td>{row.courseTitle}</td>
                  <td>
                    {row.purchaseDate ? formatDate(row.purchaseDate) : "N/A"}
                  </td>
                  <td>
                    {row.expirationDate
                      ? formatDate(row.expirationDate)
                      : "N/A"}
                    {row.purchasedCourseDocId && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() =>
                          handleExtend(
                            row.userId,
                            row.purchasedCourseDocId,
                            row.expirationDate
                          )
                        }
                        className="ml-2"
                      >
                        Adjust
                      </Button>
                    )}
                  </td>
                  <td>{row.status}</td>
                  <td className="text-center">
                    {row.expirationDate
                      ? calculateDaysLeft(row.expirationDate)
                      : "N/A"}
                  </td>
                  <td className="text-center">
                    {row.totalLessons == null || row.completedLessons == null
                      ? "N/A"
                      : row.completedLessons === 0
                      ? `0 of ${row.totalLessons}`
                      : `${row.completedLessons} of ${row.totalLessons}`}
                  </td>
                  <td className="text-center">
                    {row.purchasedCoursesCount === 0
                      ? "N/A"
                      : row.score == null
                      ? 0
                      : row.score}
                  </td>
                  <td className="text-center">
                    {row.purchasedCoursesCount == null ||
                    row.purchasedCoursesCount === 0
                      ? "N/A"
                      : row.purchasedCoursesCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Pagination>
          {[...Array(Math.ceil(filteredRows.length / rowsPerPage)).keys()].map(
            (page) => (
              <Pagination.Item
                key={page + 1}
                active={page + 1 === currentPage}
                onClick={() => paginate(page + 1)}
              >
                {page + 1}
              </Pagination.Item>
            )
          )}
        </Pagination>
      </Container>
    </div>
  );
};

export default MasterActivity;
